import type {
  ChangeRoleBody,
  ChangeRoleResponse,
  ReplaceUserBody,
  ReplaceUserResponse,
  RepresentativesResponse,
  UserInterface,
  UsersListResponse,
} from "~/api/user/interfaces";
import type { ApiResponseInterface } from "~/interfaces/errorHandler";
import { fetchWrapper } from "../fetchWrapper";

export const getUsers = async (
  params?: {
    limit?: number;
    offset?: number;
    ordering?: string;
    search?: string;
  },
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UsersListResponse> => {
  const query = params
    ? "?" +
      Object.entries(params)
        .filter(([_, v]) => v !== undefined && v !== null && v !== "")
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
        )
        .join("&")
    : "";

  try {
    const response = await fetchWrapper.get(
      `/users/${query}`,
      undefined,
      undefined,
      t,
      options,
    );

    const data = response?._data || response;

    if (
      typeof data === "object" &&
      data !== null &&
      "count" in data &&
      "results" in data &&
      "next" in data &&
      "previous" in data
    ) {
      return data as unknown as UsersListResponse;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("getUsers error:", error);
    throw error;
  }
};

export const getUserById = async (
  id: number,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface> => {
  try {
    const response = await fetchWrapper.get(
      `/users/${id}/`,
      undefined,
      undefined,
      t,
      options,
    );
    if (
      typeof response === "object" &&
      response !== null &&
      "id" in response &&
      "email" in response
    ) {
      return response as UserInterface;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("getUserById error:", error);
    throw error;
  }
};

export const updateUserById = async (
  id: number,
  userData: Partial<UserInterface>,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface> => {
  try {
    const response = await fetchWrapper.put(
      `/users/${id}/`,
      userData,
      undefined,
      t,
      options,
    );
    if (
      typeof response === "object" &&
      response !== null &&
      "id" in response &&
      "email" in response
    ) {
      return response as UserInterface;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("updateUserById error:", error);
    throw error;
  }
};

export const patchUserById = async (
  id: number,
  userData: Partial<UserInterface>,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface> => {
  try {
    const response = await fetchWrapper.patch(
      `/users/${id}/`,
      userData,
      undefined,
      t,
      options,
    );
    if (
      typeof response === "object" &&
      response !== null &&
      "id" in response &&
      "email" in response
    ) {
      return response as UserInterface;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("patchUserById error:", error);
    throw error;
  }
};

export const deleteUserById = async (
  id: number,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<void> => {
  try {
    await fetchWrapper.delete(
      `/users/${id}/`,
      undefined,
      undefined,
      t,
      options,
    );
  } catch (error) {
    console.error("deleteUserById error:", error);
    throw error;
  }
};

export const changeUserRole = async (
  roleData: ChangeRoleBody,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<ChangeRoleResponse> => {
  try {
    // Call the backend detail endpoint (PUT /users/{id}/change_role/) which
    // is simpler for the client (user id in the URL, body contains only user_type)
    const response = await fetchWrapper.put(
      `/users/${roleData.user_id}/change_role/`,
      { user_type: roleData.user_type },
      undefined,
      t,
      options,
    );

    let data: unknown = (response as ApiResponseInterface)?._data ?? response;

    if (typeof data === "object" && data !== null) {
      const obj = data as Record<string, unknown>;
      if ("_data" in obj && obj._data) data = obj._data as unknown;
      else if ("data" in obj && obj.data) data = obj.data as unknown;
      else if ("user" in obj && obj.user) data = obj.user as unknown;
    }

    const getUserTypeString = (u: unknown): string => {
      if (u === null || u === undefined) return "";
      if (typeof u === "string") return u;
      if (typeof u === "object") {
        const uu = u as Record<string, unknown>;
        if (typeof uu.value === "string") return uu.value;
        if (typeof uu.label === "string") return uu.label;
        try {
          return String(u);
        } catch {
          return "";
        }
      }
      return String(u);
    };

    if (typeof data === "object" && data !== null) {
      const obj = data as Record<string, unknown>;

      if ("user_id" in obj && "user_type" in obj) {
        return {
          user_id: Number(obj.user_id),
          user_type: getUserTypeString(obj.user_type),
        } as ChangeRoleResponse;
      }

      if ("id" in obj) {
        return {
          user_id: Number(obj.id),
          user_type: getUserTypeString(obj.user_type),
        } as ChangeRoleResponse;
      }
    }

    // If we couldn't parse the response shape, fallback to returning the roleData
    // This avoids throwing an error when backend returns an unexpected but successful payload.
    // Log a warning so maintainers can inspect the API shape.

    console.warn(
      "changeUserRole: unexpected response shape, falling back to request payload",
      { roleData, response: data },
    );
    return {
      user_id: roleData.user_id,
      user_type: roleData.user_type,
    } as ChangeRoleResponse;
  } catch (error) {
    console.error("changeUserRole error:", error);
    throw error;
  }
};

export const getCurrentUser = async (
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface> => {
  try {
    const response = await fetchWrapper.get(
      `/users/me/`,
      undefined,
      undefined,
      t,
      options,
    );
    const data = (response as ApiResponseInterface)?._data ?? response;
    if (
      typeof data === "object" &&
      data !== null &&
      "id" in data &&
      "email" in data
    ) {
      return data as UserInterface;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("getCurrentUser error:", error);
    throw error;
  }
};

export const getRepresentatives = async (
  params?: {
    limit?: number;
    offset?: number;
    ordering?: string;
    search?: string;
  },
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<RepresentativesResponse> => {
  const query = params
    ? "?" +
      Object.entries(params)
        .filter(([_, v]) => v !== undefined && v !== null && v !== "")
        .map(
          ([k, v]) =>
            `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
        )
        .join("&")
    : "";

  try {
    const response = await fetchWrapper.get(
      `/users/representatives/${query}`,
      undefined,
      undefined,
      t,
      options,
    );
    if (
      typeof response === "object" &&
      response !== null &&
      "count" in response &&
      "results" in response &&
      "next" in response &&
      "previous" in response
    ) {
      return response as unknown as RepresentativesResponse;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("getRepresentatives error:", error);
    throw error;
  }
};

export const updateCurrentUser = async (
  userData: Partial<UserInterface>,
  t?: (key: string, params?: any) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface> => {
  try {
    const response = await fetchWrapper.patch(
      `/users/update_me/`,
      userData,
      undefined,
      t,
      options,
    );
    const data = (response as ApiResponseInterface)?._data ?? response;
    if (
      typeof data === "object" &&
      data !== null &&
      "id" in data &&
      "email" in data
    ) {
      return data as UserInterface;
    }
    throw new Error("Invalid response structure from API");
  } catch (error) {
    console.error("updateCurrentUser error:", error);
    throw error;
  }
};

export const activateUser = async (
  userId: number,
  t?: (key: string, params?: Record<string, unknown>) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface | undefined> => {
  try {
    const response = await fetchWrapper.put(
      `/users/${userId}/activate/`,
      {},
      undefined,
      t,
      options,
    );
    if (
      response &&
      typeof response === "object" &&
      response !== null &&
      "id" in response &&
      "email" in response
    ) {
      return response as UserInterface;
    }
    return undefined;
  } catch (error) {
    console.error("activateUser error:", error);
    throw error;
  }
};

export const deactivateUser = async (
  userId: number,
  t?: (key: string, params?: Record<string, unknown>) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<UserInterface | undefined> => {
  try {
    const response = await fetchWrapper.delete(
      `/users/${userId}/deactivate/`,
      undefined,
      undefined,
      t,
      options,
    );
    // Se l'API restituisce dati utente, li ritorniamo
    if (
      response &&
      typeof response === "object" &&
      response !== null &&
      "id" in response &&
      "email" in response
    ) {
      return response as UserInterface;
    }
    // Altrimenti non ritorniamo nulla (void)
    return;
  } catch (error) {
    console.error("deactivateUser error:", error);
    throw error;
  }
};

export const replaceUser = async (
  userId: number,
  replaceData: ReplaceUserBody,
  t?: (key: string, params?: Record<string, unknown>) => string,
  options?: {
    showToast?: boolean;
    toastMessage?: string;
    toastErrorMessage?: string;
    toastParams?: Record<string, unknown>;
  },
): Promise<ReplaceUserResponse> => {
  try {
    const response = await fetchWrapper.post(
      `/users/${userId}/replace_representative/`,
      replaceData,
      undefined,
      t,
      options,
    );

    // Estrai i dati dalla risposta (può essere response._data o response direttamente)
    const data = (response as ApiResponseInterface)?._data ?? response;

    if (typeof data === "object" && data !== null && "message" in data) {
      return data as ReplaceUserResponse;
    }

    throw new Error(
      `Invalid response structure from API: ${JSON.stringify(response)}`,
    );
  } catch (error) {
    console.error("replaceUser error:", error);
    throw error;
  }
};
